import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { MapPin, Phone, Mail, Globe } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: "Biographie", href: "#biographie" },
    { name: "Parcours politique", href: "#parcours" },
    { name: "Réalisations", href: "#realisations" },
    { name: "Vision 2050", href: "#vision" }
  ];

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId.replace('#', ''));
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const institutionalLinks = [
    { name: "Présidence du Sénégal", href: "https://www.presidence.sn" },
    { name: "Gouvernement", href: "#" },
    { name: "Assemblée Nationale", href: "#" },
    { name: "Conseil Constitutionnel", href: "#" }
  ];

  return (
    <footer className="bg-primary text-white">
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-6xl mx-auto">
          {/* Section principale */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {/* Informations principales */}
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-6">
                <img 
                  src="/images/senegal_flag_1.jpeg" 
                  alt="Drapeau du Sénégal" 
                  className="w-12 h-8 object-cover rounded"
                />
                <div>
                  <h3 className="text-xl font-bold">Président Bassirou Diomaye Faye</h3>
                  <p className="text-white/80 text-sm">République du Sénégal</p>
                </div>
              </div>
              <p className="text-white/90 leading-relaxed mb-6">
                Site officiel dédié à Son Excellence Bassirou Diomaye Faye, 
                cinquième Président de la République du Sénégal. Découvrez sa biographie, 
                ses réalisations et sa vision pour un Sénégal souverain et prospère.
              </p>
              
              {/* Informations de contact */}
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-sm">
                  <MapPin className="w-4 h-4 text-secondary" />
                  <span>Palais de la République, Dakar, Sénégal</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <Globe className="w-4 h-4 text-secondary" />
                  <span>www.presidence.sn</span>
                </div>
              </div>
            </div>

            {/* Liens rapides */}
            <div>
              <h4 className="text-lg font-semibold mb-4 text-secondary">Liens rapides</h4>
              <ul className="space-y-2">
                {quickLinks.map((link, index) => (
                  <li key={index}>
                    <button 
                      onClick={() => scrollToSection(link.href)}
                      className="text-white/80 hover:text-secondary transition-colors text-sm text-left"
                    >
                      {link.name}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Liens institutionnels */}
            <div>
              <h4 className="text-lg font-semibold mb-4 text-secondary">Institutions</h4>
              <ul className="space-y-2">
                {institutionalLinks.map((link, index) => (
                  <li key={index}>
                    <a 
                      href={link.href}
                      className="text-white/80 hover:text-secondary transition-colors text-sm"
                      target={link.href.startsWith('http') ? '_blank' : '_self'}
                      rel={link.href.startsWith('http') ? 'noopener noreferrer' : ''}
                    >
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <Separator className="bg-white/20 mb-8" />

          {/* Citation inspirante */}
          <Card className="bg-white/10 border-white/20 mb-8">
            <CardContent className="p-6 text-center">
              <blockquote className="text-lg italic text-white/90 mb-3">
                "Je m'engage à me consacrer pleinement dans la refondation de nos institutions 
                et le renforcement des fondements de notre vivre ensemble."
              </blockquote>
              <cite className="text-secondary font-semibold">
                — Président Bassirou Diomaye Faye
              </cite>
            </CardContent>
          </Card>

          <Separator className="bg-white/20 mb-8" />

          {/* Informations légales */}
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-white/70">
            <div className="flex flex-col md:flex-row items-center gap-4">
              <p>© {currentYear} Présidence de la République du Sénégal</p>
              <div className="hidden md:block w-1 h-1 bg-white/50 rounded-full"></div>
              <p>Tous droits réservés</p>
            </div>
            <div className="flex items-center gap-4">
              <a href="#" className="hover:text-secondary transition-colors">
                Mentions légales
              </a>
              <div className="w-1 h-1 bg-white/50 rounded-full"></div>
              <a href="#" className="hover:text-secondary transition-colors">
                Politique de confidentialité
              </a>
            </div>
          </div>

          {/* Devise nationale */}
          <div className="mt-8 pt-8 border-t border-white/20 text-center">
            <p className="text-secondary font-semibold text-lg">
              "Un Peuple, Un But, Une Foi"
            </p>
            <p className="text-white/70 text-sm mt-2">
              Devise de la République du Sénégal
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;